import { useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  NativeSelect,
  OutlinedInput,
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Ti18nId } from 'hooks/useI18n';
import { setSearchQuery } from 'utils/searchQuery';
import { getSkillVideo } from 'apis/survey';
import useUser from 'store/user/useUser';
import { IUpedateSkillVideo } from 'apis/survey/type';
import useNotification from 'hooks/useNotification';
import { IPatientInfoSmall } from 'apis/admin/type';
import ClassSectionSelect from '../Assignment/ClassSectionSelect';
import useClassSection from 'store/section/useClassSection';

interface Props {
  totalCount: number;
  onRequired: (id: Ti18nId) => void;
  currentSearchType?: number;
  currentSortType?: number;
  currentSize?: number;
  setList: React.Dispatch<React.SetStateAction<IUpedateSkillVideo[]>>;
  setTotalCount: React.Dispatch<React.SetStateAction<number>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setOnlyUploadedFiles: React.Dispatch<React.SetStateAction<boolean>>;
  patientInfo: IPatientInfoSmall;
  onlyUploadedFiles?: boolean;
}

const ContentsHead = ({
  onRequired,
  currentSearchType,
  totalCount,
  currentSortType,
  currentSize = 20,
  setList,
  setTotalCount,
  setIsLoading,
  setOnlyUploadedFiles,
  patientInfo,
  onlyUploadedFiles,
}: Props) => {
  const keywordRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const searchTypeRef = useRef<HTMLSelectElement>(null);
  const sortTypeRef = useRef<HTMLSelectElement>(null);
  const sizeRef = useRef<HTMLSelectElement>(null);
  const { student_uuid: userId } = useUser();
  const { onFail } = useNotification();
  const { onResetClassSection } = useClassSection();

  const onSearch = () => {
    const keyword = keywordRef.current?.value;

    const searchType = searchTypeRef.current?.value;
    if (!keyword) return onRequired('REQUIRED.KEYWORD');

    if (searchType === '2' && isNaN(Number(keyword)))
      return onRequired('REQUIRED.NUMBER');
    if (searchType === '1' && !isNaN(Number(keyword)))
      return onRequired('REQUIRED.STRING');

    const query = isNaN(Number(keyword))
      ? setSearchQuery({
          page: 1,
          searchType,
          keyword,
        })
      : setSearchQuery({
          page: 1,
          searchType,
          keyword: String(keyword),
        });
    navigate(`${pathname}?${query}`);
  };

  const searchReset = () => {
    const size = sizeRef.current?.value || String(currentSize);

    onResetClassSection();

    getSkillVideo({
      page: '1',
      patient_id: String(patientInfo.patient_id),
      user_id: Number(userId),
      size,
      class_section_id: -1,
    })
      .then(({ data }) => {
        setList(data.student_video_info);
        setTotalCount(data.count);
      })
      .catch(e => {
        const message = `가상환자 목록을 불러오는데 실패했습니다.`;
        onFail(message, e);
      })
      .finally(() => setIsLoading(false));

    navigate('/professorAuth/video');
  };

  const onSort = () => {
    const searchType = sortTypeRef.current?.value;
    const size = sizeRef.current?.value || String(currentSize);

    const query = setSearchQuery({
      page: 1,
      sort_method: searchType,
      size,
    });

    navigate(`${pathname}?${query}`);
  };

  const SearchKeyword = () => {
    return (
      <Stack
        direction={'row'}
        alignItems="center"
        sx={{
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction={'row'} alignItems={'center'} spacing={1}>
          <ClassSectionSelect user_id={userId} patientInfo={patientInfo} />
          <Typography>|</Typography>
          <NativeSelect
            size="small"
            input={<OutlinedInput sx={{ fontSize: '12px' }} />}
            inputRef={sortTypeRef}
            defaultValue={currentSortType}
            onChange={onSort}
          >
            <option value={1}>Name (A-Z)</option>
            <option value={3}>Student ID (Ascending)</option>
            <option value={4}>Student ID (Descending)</option>
          </NativeSelect>
          <Typography>|</Typography>
          <NativeSelect
            size="small"
            input={<OutlinedInput sx={{ fontSize: '12px' }} />}
            inputRef={sizeRef}
            defaultValue={currentSize}
            onChange={onSort}
          >
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </NativeSelect>
          <Typography>|</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={onlyUploadedFiles}
                onChange={e => setOnlyUploadedFiles(e.target.checked)}
              />
            }
            label="Show only students with uploaded files"
          />
        </Stack>
        <Stack direction={'row'} alignItems={'center'} spacing={1}>
          <NativeSelect
            size="small"
            input={<OutlinedInput sx={{ fontSize: '12px', height: '33px' }} />}
            inputRef={searchTypeRef}
            defaultValue={currentSearchType}
          >
            <option value={1}>Name</option>
            <option value={2}>Student ID</option>
          </NativeSelect>

          <TextField
            placeholder="검색"
            inputRef={keywordRef}
            sx={{
              input: {
                minHeight: 33,
                boxSizing: 'border-box',
                fontSize: '12px',
              },
            }}
          />
          <Button
            variant="contained"
            onClick={onSearch}
            sx={{ height: '33px', width: '100px', fontSize: '12px' }}
          >
            Search
          </Button>
        </Stack>
      </Stack>
    );
  };

  return (
    <Box
      display="flex"
      gap="10px"
      justifyContent="space-between"
      flexDirection="column"
      sx={{ mb: 1.5 }}
    >
      <Stack direction={'row'} justifyContent={'space-between'}>
        <span style={{ color: '#6A3A87', fontSize: '22px', fontWeight: '400' }}>
          Submitted Videos
        </span>
        <Button
          variant="contained"
          onClick={searchReset}
          sx={{
            height: '33px',
            width: '100px',
            fontSize: '12px',
            lineHeight: 1,
          }}
        >
          Reset Search
        </Button>
      </Stack>
      <SearchKeyword />
    </Box>
  );
};

export default ContentsHead;
